import EvaluationInput from "@/components/Assessments/EvaluationInput";
import MultiSelect from "@/components/MultiSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import useSdgOptions from "@/hooks/useSdgOptions";
import { FormValues } from "@/schema/console/assessments/create/FormValues";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AddFilled,
  ReOrderDotsVerticalRegular,
  TargetArrowRegular,
} from "@fluentui/react-icons";
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v1 as uuid } from "uuid";

interface EditQuestionProps {
  type: "simple" | "regular";
  index: number;
  categoryIndex: number;
  subCategoryIndex: number;
}

function EditQuestion({
  type,
  index,
  categoryIndex,
  subCategoryIndex,
}: EditQuestionProps) {
  const { t } = useTranslation();
  const { sdgOptions } = useSdgOptions();

  //getfromcontext
  const { control, setValue } = useFormContext<FormValues>();

  const id = useWatch({
    control,
    name: `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.id`,
  });

  const { field: contentField } = useController({
    control,
    name: `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.content`,
  });
  const { field: perfectScoreField } = useController({
    control,
    name: `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.perfectScore`,
  });
  const { field: sdgsField } = useController({
    control,
    name: `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.sdgs`,
  });

  const {
    fields: evaluationFields,
    remove,
    append,
  } = useFieldArray({
    control,
    name: `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.evaluations`,
  });

  //dnd kit
  const { listeners, attributes, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div style={style} ref={setNodeRef}>
      <div className="flex gap-3 border-b bg-white">
        <div className="pl-4 pr-3 pt-[50px]" {...attributes} {...listeners}>
          <ReOrderDotsVerticalRegular fontSize={20} />
        </div>
        <div className="flex grow flex-col gap-4 pb-5 pr-4 pt-4">
          {/* Question content */}
          <div className="flex grow flex-col gap-2">
            <Label
              className="font-medium"
              label={t("questions")}
              htmlFor="question"
            />
            <Textarea
              id="question"
              {...contentField}
              className="grow"
              placeholder="Question"
              // onKeyDown={(e) => {
              //   if (e.key === "Enter") {
              //     setValue(
              //       `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.isEdit`,
              //       false,
              //     );
              //   }
              // }}
            />
          </div>
          {/* perfect score */}
          <div className="flex flex-col gap-2">
            <Label
              className="font-medium"
              label={t("perfect-score")}
              htmlFor="perfect-score"
            />
            <Input
              id="perfect-score"
              {...perfectScoreField}
              className="w-[100px]"
              placeholder="Point"
              // onKeyDown={(e) => {
              //   if (e.key === "Enter") {
              //     setValue(
              //       `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.isEdit`,
              //       false,
              //     );
              //   }
              // }}
            />
          </div>
          <div className="h-px bg-cool-gray-200" />
          {/* evaluation items */}
          {type === "regular" && (
            <>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <span className="grow">{t("assessor-check-items")}</span>
                  <span className="w-[96px]">{t("weight")}</span>
                  <span className="w-[35px]"></span>
                </div>
                {evaluationFields.map((evaluation, evaluationIndex) => (
                  <EvaluationInput
                    key={evaluation.id}
                    categoryIndex={categoryIndex}
                    subCategoryIndex={subCategoryIndex}
                    questionIndex={index}
                    index={evaluationIndex}
                    onRemove={() => remove(evaluationIndex)}
                  />
                ))}
                <div>
                  <Button
                    variant="text"
                    size="zero"
                    onClick={() =>
                      append({ id: uuid(), content: "", weight: 0.0 })
                    }
                  >
                    <AddFilled fontSize={20} />
                    <span>{t("add-assessor-check-item")}</span>
                  </Button>
                </div>
              </div>
              <div className="h-px bg-cool-gray-200" />
              {/* sdgs */}
              <div className="flex flex-col gap-2">
                <span className="font-medium">SDGs</span>
                <MultiSelect
                  placeholder="Select targets"
                  data={sdgOptions}
                  value={sdgsField.value}
                  onChange={(value) => {
                    sdgsField.onChange(value);
                  }}
                />
                <div className="flex flex-col gap-2">
                  {sdgsField.value.map((_, index) => (
                    <div className="flex items-center gap-2">
                      <TargetArrowRegular fontSize={20} />
                      <div>{sdgOptions[index].label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-px bg-cool-gray-200" />
            </>
          )}
          {/* done button */}
          <div className="flex justify-end">
            <Button
              onClick={() =>
                setValue(
                  `categories.${categoryIndex}.subcategories.${subCategoryIndex}.questions.${index}.isEdit`,
                  false,
                )
              }
            >
              {t("done")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditQuestion;
